import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, XCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { syncSubscription } from "../lib/checkout";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | timeout

  useEffect(() => {
    let attempts = 0;
    let active = true;

    const poll = async () => {
      if (!active) return;
      try {
        const res = await syncSubscription();
        if (res?.subscribed) {
          sessionStorage.setItem("subSynced", "1");
          refresh();
          setState("paid");
          return;
        }
      } catch (e) {
        // keep trying — the webhook is the backup
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-5">
      <div className="grain-overlay" />
      <div
        data-testid="payment-success-page"
        className="relative max-w-lg w-full border border-white/15 bg-gradient-to-b from-white/[0.04] to-transparent p-10 md:p-14 text-center"
      >
        {state === "checking" && (
          <>
            <Loader2 className="h-8 w-8 text-blood animate-spin mx-auto" />
            <p className="mt-6 font-mono text-[11px] tracking-[0.3em] uppercase text-muted-foreground">
              Confirming your subscription…
            </p>
          </>
        )}

        {state === "paid" && (
          <div data-testid="payment-confirmed">
            <div className="flex items-center justify-center gap-3 text-blood">
              <Check className="h-6 w-6" />
              <span className="font-mono text-xs tracking-[0.3em] uppercase">
                Subscription active
              </span>
            </div>
            <h1 className="mt-6 font-serif text-5xl md:text-6xl leading-[0.9] tracking-tighter text-bone">
              Welcome to the <span className="italic text-blood">dark.</span>
            </h1>
            <p className="mt-5 text-sm md:text-base text-muted-foreground leading-relaxed">
              You're all access now — the full archive is yours. The projector
              is warming up.
            </p>
            <Link
              to="/archive"
              data-testid="enter-archive-link"
              className="mt-8 inline-flex items-center justify-center gap-2 bg-blood text-white px-7 py-3.5 font-mono text-[11px] tracking-[0.25em] uppercase transition-transform duration-300 hover:-translate-y-0.5"
            >
              Enter the archive
            </Link>
          </div>
        )}

        {(state === "timeout" || state === "error") && (
          <>
            <XCircle className="h-8 w-8 text-blood mx-auto" />
            <h1 className="mt-6 font-serif text-4xl tracking-tighter text-bone">
              Still processing
            </h1>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              We couldn't confirm the payment just yet. If you were charged,
              your access will activate shortly — no need to pay again.
            </p>
          </>
        )}

        <Link
          to="/"
          data-testid="back-home-link"
          className="mt-9 inline-flex items-center justify-center gap-2 border border-white/20 hover:border-blood hover:bg-blood px-7 py-3.5 font-mono text-[11px] tracking-[0.25em] uppercase transition-all duration-300"
        >
          Back to Old Geezer
        </Link>
      </div>
    </div>
  );
}
